import React, {
    Dispatch,
    SetStateAction,
    createContext,
    useContext,
    useEffect,
    useMemo,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import {threadType} from '../types/thread'
import {AssistantApi} from '../services/AssistantApi.service'
import {messageType} from '../types/message'
import {TribunaisType} from '../types/tribunais'
import {AssistantType} from '../types/assistant'
import {CookiesService} from '../services/Cookie.service'
import {limitsType} from '../types/limits'
import {project} from '../types/project'
import {Question} from '../types/question'
import {Organization} from '../types/orgUsers'
import {OrgMetrics} from '../types/orgUsersMetrics'
import {DocumentApi} from '../services/DocumentApi.service'
import {PriceResearchApiService} from '../services/PriceResearchApi.service'
import {PriceSearchOut} from '../types/priceResearch'
import {webSocketMessage} from '../types/websocket'
import {AuthContext} from './auth'
import {userDocumentsInfo} from '../types/userDocumentsInfo'
import {IUserInfo} from '../types/user'
import {FlowApi} from '../services/FlowApi.service'
import {ProjectType, QuestionFlow} from 'types/flowData'
import {useNavigate, useLocation} from 'react-router-dom'
import {toast} from 'react-toastify'
import {Area, applyArea, hasAreas} from '../utils/colors'

const MODULE_NOTIFICATION_CONFIG: Record<
    string,
    {routes: string[]; primaryRoute: string; label: string}
> = {
    'document-analyzer': {
        routes: ['/analisador'],
        primaryRoute: '/analisador',
        label: 'Analisador de Documentos',
    },
    'ata-inteligente': {
        routes: ['/ata-inteligente'],
        primaryRoute: '/ata-inteligente',
        label: 'Ata Inteligente',
    },
    'document-generator': {
        routes: ['/gerador', '/espera', '/projetos'],
        primaryRoute: '/projetos',
        label: 'Gerador de Documentos',
    },
    'price-research': {
        routes: ['/pesquisa-precos'],
        primaryRoute: '/pesquisa-precos',
        label: 'Pesquisa de Preços',
    },
    'radar-fornecedor': {
        routes: ['/radar-fornecedor'],
        primaryRoute: '/radar-fornecedor',
        label: 'Radar de Fornecedor',
    },
    'levantamento-mercado': {
        routes: ['/levantamento-mercado'],
        primaryRoute: '/levantamento-mercado',
        label: 'Levantamento de Mercado',
    },
    chat: {
        routes: ['/'],
        primaryRoute: '/',
        label: 'Assistente',
    },
    'editor-template-flow': {
        routes: ['/upload-documents'],
        primaryRoute: '/upload-documents',
        label: 'Editor de Templates',
    },
}

// Push do módulo Levantamento de Mercado — espelha o LevantamentoStatusNotify
// do user-notifier: {status, levantamento_id, timestamp, description, source_url,
// module: 'levantamento-mercado'}.
export interface LevantamentoSocketMessage {
    status: string
    levantamento_id: string
    timestamp: string
    description: string | null
    source_url: string | null
    module?: string
}

const limitsTemplate = {
    created_at: '',
    usages: {
        created_at: '',
        messages: {
            max: 1,
            current: 1,
        },
        projects: {
            max: 0,
            current: 0,
        },
    },
}

interface SubscriptionType {
    created_at: string
    guid: string
    issued_at: string
    exp_at: string
    is_active: boolean
    price: number
    stripe_subscription_id: string
    pending_activation: boolean
}

export interface Project {
    created_at: string
    name: string
    description: string
    updated_at: string
    start_date: string
    end_date: string
    is_active: boolean
    project_type_id: string
    status: string
}

interface IHistoryContext {
    setUnarchieveModal: Dispatch<SetStateAction<boolean>>
    selected: threadType | null
    setSelected: Dispatch<SetStateAction<threadType | null>>
    historyArray: threadType[]
    createNewChat: () => Promise<void>
    messagesArray: messageType[]
    includeMessageAtArray: (message: messageType) => void
    tribunal: TribunaisType
    setTribunal: Dispatch<SetStateAction<TribunaisType>>
    assistant: AssistantType | null
    setAssistant: Dispatch<SetStateAction<AssistantType | null>>
    limits: limitsType
    assistants: AssistantType[]
    getLimits: () => void
    archiveThread: (guid: string) => void
    unarchiveThread: (guid: string) => void
    deleteThread: (guid: string) => void
    renameThread: (guid: string, name: string) => Promise<void>
    updateCurrentThread: (message: messageType) => void
    updateThreadUploadedDocuments: (
        guid: string,
        uploaded_documents: number
    ) => void
    updateThreadPageCount: (guid: string, page_count: number) => void
    subscription: SubscriptionType | null
    alertVisibility: string
    archivedThread?: threadType[]
    goToArchivedThreadsPage: (page: number) => void
    archivedThreadPage: number
    archivedProjects?: project[]
    goToArchivedProjectsPage: (page: number) => void
    archivedProjectsPage: number
    unarchieveModal: boolean
    isChat: boolean
    area: Area
    setArea: Dispatch<SetStateAction<Area>>
    projectTypes: ProjectType[]
    projectTypeFilterId: string | undefined
    setIsChat: Dispatch<SetStateAction<boolean>>
    projectArray: project[]
    questionArray: Question[]
    setQuestionArray: Dispatch<SetStateAction<Question[]>>
    getNextQuestion: (project_id: string) => void
    getProjects: () => void
    usersOrg: Organization[]
    setMessagesArray: Dispatch<SetStateAction<messageType[]>>
    postProject: (projectData?: any) => Promise<void>
    archiveProject: (project_id: string) => void
    unarchiveProject: (project_id: string) => void
    archivedPesquisas: PriceSearchOut[]
    setArchivedPesquisas: Dispatch<SetStateAction<PriceSearchOut[]>>
    archivePesquisa: (id: string) => void
    unarchivePesquisa: (id: string) => void
    getArchivedPesquisas: () => void
    goToArchivedPesquisasPage: (page: number) => void
    archivedPesquisasPage: number
    webSocketMessage: webSocketMessage
    openModelAlertLimit: boolean
    setOpenModelAlertLimit: Dispatch<SetStateAction<boolean>>
    orgUsersMetrics: OrgMetrics
    getOrgUsersMetrics: () => void
    loadingMessageBot: boolean
    setLoadingMessageBot: Dispatch<SetStateAction<boolean>>
    userDocumentsInfo: userDocumentsInfo | null
    uuid: string
    setUuid: Dispatch<SetStateAction<string>>
    updateProject: (projectData?: any) => Promise<void>
    setOrganizationMaxUsages: (
        metricas: {[guid: string]: {messages?: number; projects?: number}},
        splitResources: boolean,
        splitDocs: boolean
    ) => Promise<void>
    projectCost: number
    setProjectCost: Dispatch<SetStateAction<number>>
    selectedFiles: File[]
    setSelectedFiles: Dispatch<SetStateAction<File[]>>
    user: IUserInfo | null
    viewResume: boolean
    setViewResume: Dispatch<SetStateAction<boolean>>
    redSocketMessage: webSocketMessage
    setRedSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    priceResearchSocketMessage: webSocketMessage
    setPriceResearchSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    // Conexão do WebSocket: quem depende de push alonga o polling com ela aberta e relê
    // o estado a cada reconexão (o que mudou durante a queda não é reenviado).
    wsAberto: boolean
    wsReconexoes: number
    caronaSocketMessage: webSocketMessage
    setCaronaSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    radarSocketMessage: webSocketMessage
    setRadarSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    assistenteSocketMessage: webSocketMessage
    setAssistenteSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    documentUploadSocketMessage: webSocketMessage
    setDocumentUploadSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    tryGetAnswersSocketMessage: webSocketMessage
    setTryGetAnswersSocketMessage: Dispatch<SetStateAction<webSocketMessage>>
    levantamentoSocketMessage: LevantamentoSocketMessage
    setLevantamentoSocketMessage: Dispatch<
        SetStateAction<LevantamentoSocketMessage>
    >
    flowQuestions: QuestionFlow | null
    setFlowData: (id: string) => Promise<void>
    flows: Array<{id: string; name: string}>
    setFlows: Dispatch<SetStateAction<Array<{id: string; name: string}>>>
    updateFlow: (flowData: any) => Promise<void>
    getFlow: () => void
    pendingChatExports: string[]
    registerChatExport: (exportId: string) => void
}

const CHAT_EXPORT_TIMEOUT_MS = 20 * 60 * 1000 // igual ao backend (FAILED apos 20 min)
const CHAT_EXPORT_TIMEOUT_ERROR =
    'O arquivo ainda está sendo gerado. Tente novamente em instantes.'
const CHAT_EXPORT_ERROR = 'Não foi possível gerar o arquivo. Tente novamente.'

export const HistoryContext = createContext<IHistoryContext>({
    pendingChatExports: [],
    registerChatExport: () => {},
    selected: {
        owner_id: '0',
        guid: '0',
        name: '',
        created_at: '',
        last_updated: '',
        uploaded_documents: 0,
    },
    setUnarchieveModal: () => {},
    setIsChat: () => {},
    setSelected: () => {},
    historyArray: [],
    archivedThread: [],
    goToArchivedThreadsPage: () => {},
    archivedThreadPage: 1,
    archivedProjects: [],
    goToArchivedProjectsPage: () => {},
    archivedProjectsPage: 1,
    createNewChat: () => {
        return Promise.resolve()
    },
    messagesArray: [],
    includeMessageAtArray: () => {},
    tribunal: 'Tribunais Superiores',
    setTribunal: () => {},
    assistant: null,
    setAssistant: () => {},
    limits: limitsTemplate,
    assistants: [],
    subscription: null,
    getLimits: () => {},
    archiveThread: () => {},
    unarchiveThread: () => {},
    deleteThread: () => {},
    renameThread: () => {
        return Promise.resolve()
    },
    updateCurrentThread: () => {},
    updateThreadUploadedDocuments: () => {},
    updateThreadPageCount: () => {},
    alertVisibility: 'transparent',
    unarchieveModal: false,
    isChat: true,
    area: 'licitacao',
    setArea: () => {},
    projectTypes: [],
    projectTypeFilterId: undefined,
    projectArray: [],
    questionArray: [],
    setQuestionArray: () => {},
    getNextQuestion: () => {},
    getProjects: () => {},
    setMessagesArray: () => {},
    postProject: () => {
        return Promise.resolve()
    },
    archiveProject: () => {},
    unarchiveProject: () => {},
    archivedPesquisas: [],
    setArchivedPesquisas: () => {},
    archivePesquisa: () => {},
    unarchivePesquisa: () => {},
    getArchivedPesquisas: () => {},
    goToArchivedPesquisasPage: () => {},
    archivedPesquisasPage: 1,
    usersOrg: [],
    webSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    openModelAlertLimit: false,
    setOpenModelAlertLimit: () => {},
    orgUsersMetrics: {
        org_name: '',
        org_guid: '',
        split_resources: false,
        split_docs: false,
        max_answers: 0,
        used_answers: 0,
        max_docs: 0,
        used_docs: 0,
        users_metrics: [],
    },
    getOrgUsersMetrics: () => {},
    loadingMessageBot: false,
    setLoadingMessageBot: () => {},
    userDocumentsInfo: null,
    uuid: '',
    setUuid: () => {},
    updateProject: () => {
        return Promise.resolve()
    },
    setOrganizationMaxUsages: () => {
        return Promise.resolve()
    },
    projectCost: 0,
    setProjectCost: () => {},
    selectedFiles: [],
    setSelectedFiles: () => {},
    user: null,
    viewResume: false,
    setViewResume: () => {},
    redSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setRedSocketMessage: () => {},
    wsAberto: false,
    wsReconexoes: 0,
    priceResearchSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setPriceResearchSocketMessage: () => {},
    caronaSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setCaronaSocketMessage: () => {},
    radarSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setRadarSocketMessage: () => {},
    assistenteSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setAssistenteSocketMessage: () => {},
    documentUploadSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setDocumentUploadSocketMessage: () => {},
    tryGetAnswersSocketMessage: {
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    },
    setTryGetAnswersSocketMessage: () => {},
    levantamentoSocketMessage: {
        status: '',
        levantamento_id: '',
        timestamp: '',
        description: null,
        source_url: null,
    },
    setLevantamentoSocketMessage: () => {},
    flowQuestions: null,
    setFlowData: () => {
        return Promise.resolve()
    },
    flows: [],
    setFlows: () => {},
    updateFlow: () => {
        return Promise.resolve()
    },
    getFlow: () => {},
})

// Duplicado de UPLOAD_PLACEHOLDER_CONTENT em
// core-schief-ai/app/services/threads.py — manter em sincronia.
const UPLOAD_PLACEHOLDER_CONTENT = 'Thread iniciada com documento'

const HistoryProvider = ({
    children,
}: {
    children: React.ReactNode
}): JSX.Element => {
    const {userInfo} = useContext(AuthContext)
    const navigate = useNavigate()
    const location = useLocation()
    const locationRef = useRef(location)
    const navigateRef = useRef(navigate)

    useEffect(() => {
        locationRef.current = location
    }, [location])

    useEffect(() => {
        navigateRef.current = navigate
    }, [navigate])

    const [selected, setSelected] = useState<threadType | null>(null)
    const [pendingChatExports, setPendingChatExports] = useState<string[]>([])
    const pendingExportsRef = useRef(
        new Map<string, ReturnType<typeof setTimeout>>()
    )
    const [assistants, setAssistants] = useState<AssistantType[]>([])
    const [assistant, setAssistant] = useState<AssistantType | null>(null)
    const [historyArray, setHistoryArray] = useState<threadType[]>([])
    const [projectArray, setProjectArray] = useState<project[]>([])
    const [questionArray, setQuestionArray] = useState<Question[]>([])
    const [messagesArray, setMessagesArray] = useState<messageType[]>([])
    const [limits, setLimits] = useState<limitsType>(limitsTemplate)
    const [openModelAlertLimit, setOpenModelAlertLimit] =
        useState<boolean>(false)
    const [archivedThread, setArchivedThread] = useState<threadType[]>([])
    const [archivedThreadPage, setArchivedThreadPage] = useState(1)
    const [archivedProjects, setArchivedProjects] = useState<project[]>([])
    const [archivedProjectsPage, setArchivedProjectsPage] = useState(1)
    const [archivedPesquisas, setArchivedPesquisas] = useState<
        PriceSearchOut[]
    >([])
    const [archivedPesquisasPage, setArchivedPesquisasPage] = useState(1)
    const [usersOrg, setUsersOrg] = useState<Organization[]>([])
    const [orgUsersMetrics, setorgUsersMetrics] = useState<OrgMetrics>({
        org_name: '',
        org_guid: '',
        split_resources: false,
        split_docs: false,
        max_answers: 0,
        used_answers: 0,
        max_docs: 0,
        used_docs: 0,
        users_metrics: [],
    })
    const [tribunal, setTribunal] = useState<TribunaisType>(
        'Tribunais Superiores'
    )
    const [subscription, setSubscription] = useState<SubscriptionType | null>(
        null
    )
    const [alertVisibility, setAlertVisibility] =
        useState<string>('transparent')
    const [unarchieveModal, setUnarchieveModal] = useState<boolean>(false)
    const [isChat, setIsChat] = useState<boolean>(true)
    const [area, setArea] = useState<Area>('licitacao')
    useEffect(() => applyArea(area), [area])
    const [loadingMessageBot, setLoadingMessageBot] = useState<boolean>(false)
    const api = useMemo(() => new AssistantApi(), [])
    const documentApi = useMemo(() => new DocumentApi(), [])
    const [projectTypes, setProjectTypes] = useState<ProjectType[]>([])
    useEffect(() => {
        if (!hasAreas()) return
        documentApi
            .getOrganizationProjectTypes()
            .then((data) => Array.isArray(data) && setProjectTypes(data))
            .catch((error) => console.error('Erro ao carregar project types:', error))
    }, [documentApi])
    // só filtra quando a org tem mais de um project type; senão lista tudo
    const projectTypeFilterId =
        projectTypes.length > 1
            ? projectTypes.find((pt) => (pt.value ?? pt.id) === area)?.id
            : undefined
    const flowApi = useMemo(() => new FlowApi(), [])
    const priceResearchApi = useMemo(() => new PriceResearchApiService(), [])
    const cookieService = new CookiesService()
    const [webSocketMessage, setWebSocketMessage] = useState<webSocketMessage>({
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    })
    const [redSocketMessage, setRedSocketMessage] = useState<webSocketMessage>({
        status: '',
        project_id: '',
        timestamp: '',
        description: '',
        doc_url: '',
    })
    const [wsAberto, setWsAberto] = useState(false)
    const [wsReconexoes, setWsReconexoes] = useState(0)
    const [priceResearchSocketMessage, setPriceResearchSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [caronaSocketMessage, setCaronaSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [radarSocketMessage, setRadarSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [assistenteSocketMessage, setAssistenteSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [documentUploadSocketMessage, setDocumentUploadSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [tryGetAnswersSocketMessage, setTryGetAnswersSocketMessage] =
        useState<webSocketMessage>({
            status: '',
            project_id: '',
            timestamp: '',
            description: '',
            doc_url: '',
        })
    const [levantamentoSocketMessage, setLevantamentoSocketMessage] =
        useState<LevantamentoSocketMessage>({
            status: '',
            levantamento_id: '',
            timestamp: '',
            description: null,
            source_url: null,
        })
    const [userDocumentsInfo, setUserDocumentsInfo] =
        useState<userDocumentsInfo | null>(null)
    const [uuid, setUuid] = useState<string>('')
    const [projectCost, setProjectCost] = useState<number>(0)
    const [selectedFiles, setSelectedFiles] = useState<File[]>([])
    const [user, setUser] = useState<IUserInfo | null>(null)
    const [viewResume, setViewResume] = useState<boolean>(false)
    const [flowQuestions, setflowQuestions] = useState<QuestionFlow | null>(
        null
    )
    const [flows, setFlows] = useState<Array<{id: string; name: string}>>([])

    async function getSubscription() {
        try {
            const res = await api.getCurrentSubscription()
            setSubscription(res)
        } catch (error) {
            console.error('Erro ao obter subscription:', error)
        }
    }

    function isQuestion(obj: any): obj is Question {
        return (
            obj &&
            typeof obj.id === 'string' &&
            typeof obj.content === 'string' &&
            obj.type &&
            (obj.type.select ||
                obj.type.text ||
                obj.type.radio ||
                obj.type.table)
        )
    }

    async function getNextQuestion(project_id: string) {
        try {
            const res = await documentApi.getNextQuestion(project_id)

            if (isQuestion(res)) {
                setQuestionArray((prevItems) => [...prevItems, res])
                console.log('Nova pergunta adicionada:', res)
            } else {
                console.error(
                    'Resposta não corresponde à tipagem de Question:',
                    res
                )
            }
        } catch (error) {
            console.error('Erro ao buscar próxima pergunta:', error)
        }
    }

    async function getProjects() {
        try {
            const res = await documentApi.getProjects(15, 0, projectTypeFilterId)
            setProjectArray(res)
        } catch (error) {
            console.error('Erro ao buscar projetos:', error)
        }
    }

    async function postProject(projectData: any) {
        try {
            const res = await documentApi.postProject(projectData)
            setQuestionArray([res])
            console.log('Projeto criado:', questionArray)
        } catch (error) {
            console.error('Erro ao criar projeto:', error)
        }
    }

    async function updateProject(projectData: any) {
        try {
            await documentApi.updateProject(projectData)
            getProjects()
            console.log('Projeto atualizado:', questionArray)
        } catch (error) {
            console.error('Erro ao atualizar projeto:', error)
        }
    }

    async function getFlow() {
        try {
            const res = await flowApi.getFlow()
            setFlows(res)
        } catch (error) {
            console.error('Erro ao buscar flow do projeto:', error)
        }
    }

    async function setFlowData(id: string) {
        const flowById = await flowApi.getFlowById(id)
        setflowQuestions(flowById)
    }

    async function updateFlow(flowData: any) {
        try {
            await flowApi.updateFlow(flowData)
        } catch (error) {
            console.error('Erro ao atualizar fluxo de documentos:', error)
        }
    }

    async function archiveProject(project_id: string) {
        try {
            await documentApi.archiveProject(project_id)
            getProjects()
            getArchivedProjects()
        } catch (error) {
            console.error('Erro ao arquivar projeto:', error)
        }
    }

    async function unarchiveProject(project_id: string) {
        try {
            await documentApi.unarchiveProject(project_id)
            getProjects()
            getArchivedProjects()
        } catch (error) {
            console.error('Erro ao desarquivar projeto:', error)
        }
    }

    async function getArchivedProjects() {
        try {
            const res = await documentApi.getArchivedProjects()
            setArchivedProjects(res)
            setArchivedProjectsPage(1)
        } catch (error) {
            console.error('Erro ao buscar projetos arquivados:', error)
        }
    }

    async function goToArchivedProjectsPage(page: number) {
        const limit = 15
        try {
            const res = await documentApi.getArchivedProjects(
                limit,
                (page - 1) * limit
            )
            setArchivedProjects(res)
            setArchivedProjectsPage(page)
        } catch (error) {
            console.error('Erro ao buscar projetos arquivados:', error)
        }
    }

    async function archivePesquisa(id: string) {
        try {
            await priceResearchApi.arquivarPesquisa(id)
            getArchivedPesquisas()
        } catch (error) {
            console.error('Erro ao arquivar pesquisa:', error)
        }
    }

    async function unarchivePesquisa(id: string) {
        try {
            await priceResearchApi.desarquivarPesquisa(id)
            getArchivedPesquisas()
        } catch (error) {
            console.error('Erro ao desarquivar pesquisa:', error)
        }
    }

    async function getArchivedPesquisas() {
        try {
            const res = await priceResearchApi.listArquivadas()
            setArchivedPesquisas(res.pesquisas)
            setArchivedPesquisasPage(1)
        } catch (error) {
            console.error('Erro ao buscar pesquisas arquivadas:', error)
        }
    }

    async function goToArchivedPesquisasPage(page: number) {
        const limit = 15
        try {
            const res = await priceResearchApi.listArquivadas(
                limit,
                (page - 1) * limit
            )
            setArchivedPesquisas(res.pesquisas)
            setArchivedPesquisasPage(page)
        } catch (error) {
            console.error('Erro ao buscar pesquisas arquivadas:', error)
        }
    }

    async function createNewChat(forceCreate: boolean = false): Promise<void> {
        console.log('Criando novo chat...')
        if (!forceCreate && historyArray.find((item) => item.guid === 'new')) {
            if (
                historyArray.findIndex(
                    (item) => item.guid === selected?.guid
                ) === 0
            ) {
                triggerAlertVisibility()
            } else
                setSelected(
                    historyArray[
                        historyArray.findIndex((item) => item.guid === 'new')
                    ]
                )
            return
        }
        const newChat: threadType = {
            created_at: new Date(),
            last_updated: new Date(),
            guid: 'new',
            name: 'Novo Chat',
            owner_id: '',
            uploaded_documents: 0,
        }
        setHistoryArray((prev) => [newChat, ...prev])
        console.log('Novo chat criado:', newChat)
        setSelected(newChat)
        return
    }

    async function archiveThread(guid: string) {
        await api.archiveThread(guid)
        getThreadsArchived()
        await getThreads()
    }

    async function unarchiveThread(guid: string) {
        await api.unarchiveThread(guid)
        await getThreadsArchived()
        await getThreads()
    }

    async function deleteThread(guid: string) {
        await api.deleteThread(guid)
        await getThreads()
    }

    async function renameThread(guid: string, name: string) {
        const renamed = await api.renameThread(guid, name)
        // Atualiza no lugar em vez de refazer o getThreads() — refetch reseta a
        // paginação do histórico e o scroll do usuário.
        setHistoryArray((prev) =>
            prev.map((thread) =>
                thread.guid === guid ? {...thread, name: renamed.name} : thread
            )
        )
        setSelected((prev) =>
            prev?.guid === guid ? {...prev, name: renamed.name} : prev
        )
        setArchivedThread((prev) =>
            prev.map((thread) =>
                thread.guid === guid ? {...thread, name: renamed.name} : thread
            )
        )
    }

    function updateHistoryArrayAtIndex(index: number, updatedChat: threadType) {
        if (index !== -1) {
            setHistoryArray((prev) => {
                const newArray = [...prev]
                newArray[index] = updatedChat
                return newArray
            })
        }
    }

    // Atualiza só a lista (historyArray/archivedThread), nunca `selected` —
    // trocar a referência de `selected` enquanto guid ainda é 'new' dispara
    // o useEffect([selected]) e reseta messagesArray (ver commit f2f01c8).
    function updateThreadUploadedDocuments(
        guid: string,
        uploaded_documents: number
    ) {
        setHistoryArray((prev) =>
            prev.map((thread) =>
                thread.guid === guid
                    ? {...thread, uploaded_documents}
                    : thread
            )
        )
        setArchivedThread((prev) =>
            prev.map((thread) =>
                thread.guid === guid
                    ? {...thread, uploaded_documents}
                    : thread
            )
        )
    }

    // Mesma ressalva do updateThreadUploadedDocuments acima: nunca atualiza
    // `selected` diretamente por aqui (só historyArray/archivedThread).
    function updateThreadPageCount(guid: string, page_count: number) {
        setHistoryArray((prev) =>
            prev.map((thread) =>
                thread.guid === guid ? {...thread, page_count} : thread
            )
        )
        setArchivedThread((prev) =>
            prev.map((thread) =>
                thread.guid === guid ? {...thread, page_count} : thread
            )
        )
    }

    async function updateCurrentThread(message: messageType) {
        console.log('Atualizando thread atual com a mensagem:', message)
        const currentLimits = await api.getLimits()
        setLimits(currentLimits)
        if (
            currentLimits?.usages?.messages?.current ===
            currentLimits?.usages?.messages?.max
        ) {
            setOpenModelAlertLimit(true)
            return
        }
        var threadName = 'Novo Chat'
        if (selected?.guid === 'new' && message.thread_id !== 'new') {
            const thread = await api.getThread(message.thread_id)

            threadName = thread.name
        }
        const updatedChat: threadType = {
            created_at: new Date(),
            last_updated: new Date(),
            guid: message.thread_id,
            name: threadName,
            owner_id: '',
            uploaded_documents: 0,
        }
        const newThreadIndex = historyArray.findIndex(
            (item) => item.guid === 'new'
        )
        updateHistoryArrayAtIndex(newThreadIndex, updatedChat)
        // Marca a thread na lateral assim que o guid real é conhecido —
        // não precisa esperar a resposta do assistente pra isso, já que o
        // guid é gerado no cliente no momento do envio (ver sendMessage
        // em chat/input/input.tsx).
        setSelected(updatedChat)
        if (!message.author_id) {
            await getThreads()
        }
    }
    function includeMessageAtArray(message: messageType) {
        if (message.author_id == null && message.assistant_id == null) {
            console.warn('Mensagem inválida - sem author_id e sem assistant_id')
            return
        }

        setSelected((currentSelected) => {
            if (currentSelected?.guid === 'new') {
                setMessagesArray(() => [message])
                updateCurrentThread(message)
            } else {
                if (currentSelected?.guid === message.thread_id) {
                    setMessagesArray((prev) => {
                        const messageExists = prev.some(
                            (msg) =>
                                new Date(msg.created_at).getTime() ===
                                new Date(message.created_at).getTime()
                        )

                        return messageExists ? prev : [...prev, message]
                    })
                }
                // updateCurrentThread(message)
            }
            return currentSelected
        })
    }

    function triggerAlertVisibility() {
        setAlertVisibility('red')
        setTimeout(() => {
            setAlertVisibility('transparent')
        }, 1500)
    }

    // O backend cria a mensagem do usuario com um texto placeholder quando ha
    // arquivos anexados (conteudo real e documentos so sao gravados depois do
    // callback do uploader, um arquivo por vez). A mensagem otimista local usa
    // o mesmo `guid` enviado ao backend (ver chat/input/input.tsx), entao o
    // casamento e exclusivamente por `guid`. So enquanto o conteudo do servidor
    // ainda e o placeholder: conteudo = local; documentos = uniao por document_id (servidor
    // vence, locais ausentes no servidor sao anexados); uploaded_documents =
    // uniao por basename (servidor primeiro).
    function mergeUploadPlaceholders(
        local: messageType[],
        server: messageType[]
    ): messageType[] {
        const basename = (path: string) => path.split('/').pop() || path
        return server.map((m) => {
            // Conteudo real = callbacks todos processados: servidor autoritativo
            if (!m.guid || m.content !== UPLOAD_PLACEHOLDER_CONTENT) return m
            const match = local.find(
                (l) =>
                    l.guid === m.guid &&
                    l.content !== UPLOAD_PLACEHOLDER_CONTENT
            )
            if (!match) return m
            const serverDocs = m.documents || []
            const documents = [
                ...serverDocs,
                ...(match.documents || [])
                    .filter(
                        (d) =>
                            !serverDocs.some(
                                (sd) => sd.document_id === d.document_id
                            )
                    )
                    // mantem o flag local: so continua pending quem ja era
                    // (docs locais ja confirmados nao voltam a pending)
                    .map((d) => ({...d})),
            ]
            const serverUploaded = m.uploaded_documents || []
            const uploaded_documents = [
                ...serverUploaded,
                ...(match.uploaded_documents || []).filter(
                    (u) =>
                        !serverUploaded.some(
                            (su) => basename(su) === basename(u)
                        )
                ),
            ]
            return {
                ...m,
                content: match.content,
                documents,
                uploaded_documents,
            }
        })
    }

    async function getMessages(options?: {keepOnError?: boolean}) {
        if (selected?.guid && api && selected?.guid !== 'new') {
            const requestedGuid = selected.guid
            const requestId = ++getMessagesRequestIdRef.current
            api.getMessages(requestedGuid)
                .then((response) => {
                    // ignora resposta de uma thread que deixou de ser a selecionada
                    if (selectedRef.current?.guid !== requestedGuid) return
                    // resposta fora de ordem: ja existe um fetch mais novo
                    if (requestId !== getMessagesRequestIdRef.current) return
                    response?.length === 0
                        ? setMessagesArray([
                              {
                                  content: 'Olá, como posso ajudar?',
                                  thread_id: '',
                                  assistant_id: 'schief',
                                  author_id: '',
                                  created_at: new Date(),
                                  uploaded_documents: [],
                              },
                          ])
                        : setMessagesArray((prev) =>
                              mergeUploadPlaceholders(prev, response)
                          )
                })
                .catch((err) => {
                    if (selectedRef.current?.guid !== requestedGuid) return
                    if (requestId !== getMessagesRequestIdRef.current) return
                    if (options?.keepOnError) return
                    setMessagesArray([
                        {
                            content: 'Olá, como posso ajudar?',
                            thread_id: '',
                            assistant_id: 'schief',
                            author_id: '',
                            created_at: new Date(),
                            uploaded_documents: [],
                        },
                    ])
                })
        } else {
            if (
                messagesArray.length === 0 ||
                messagesArray[0]?.thread_id !== selected?.guid
            ) {
                selected?.guid === 'new'
                    ? setMessagesArray([
                          {
                              content: 'Olá, como posso ajudar?',
                              thread_id: '',
                              assistant_id: 'schief',
                              author_id: '',
                              created_at: new Date(),
                              uploaded_documents: [],
                          },
                      ])
                    : setMessagesArray([])
            }
        }
    }

    async function getLimits() {
        return api
            .getLimits()
            .then((response: limitsType) => setLimits(response))
    }

    async function getThreads() {
        return api.getThreads().then((response: threadType[]) => {
            setHistoryArray(response)
            createNewChat(true)
        })
    }

    async function getAssistants() {
        return api.getAssistants().then((res) => {
            const assistantList = Array.isArray(res) ? res : []
            setAssistants(assistantList)
            const cookieService = new CookiesService()
            const c_assistant = cookieService.getAssistant()
            if (c_assistant) {
                setAssistant(c_assistant)
            } else {
                setAssistant(assistantList[0])
            }
        })
    }

    async function getThreadsArchived() {
        return api.getThreadsArchived().then((response: threadType[]) => {
            setArchivedThread(response)
            setArchivedThreadPage(1)
        })
    }

    async function goToArchivedThreadsPage(page: number) {
        const limit = 15
        try {
            const res = await api.getThreadsArchived(
                limit,
                (page - 1) * limit
            )
            setArchivedThread(res)
            setArchivedThreadPage(page)
        } catch (error) {
            console.error('Erro ao buscar chats arquivados:', error)
        }
    }

    async function getOrgUsers() {
        return api.getOrganizationUsers().then((response: Organization[]) => {
            setUsersOrg(response)
        })
    }

    async function setOrganizationMaxUsages(
        metricas: {[guid: string]: {messages?: number; projects?: number}},
        splitResources: boolean,
        splitDocs: boolean
    ) {
        const users_usages = Object.entries(metricas).map(([guid, usages]) => ({
            guid,
            max_messages: usages.messages ?? 0,
            max_documents: usages.projects ?? 0,
        }))

        const payload = {
            split_resources: splitResources,
            split_docs: splitDocs,
            users_limits: users_usages,
        }

        try {
            await api.setOrganizationMaxUsages(payload)
            await getLimits()
        } catch (error) {
            console.log(error)
            throw error
        }
    }

    async function getOrgUsersMetrics() {
        try {
            console.log('Buscando métricas dos usuários da organização...')
            const response = await api.getOrganizationUsersMetrics()
            setorgUsersMetrics(response)
        } catch (error) {
            console.error(
                'Erro ao buscar métricas dos usuários da organização:',
                error
            )
        }
    }

    async function getUserInfo() {
        try {
            const response = await documentApi.getUserInfo()
            setUserDocumentsInfo(response)
        } catch (error) {
            console.error('Erro ao buscar informações do usuário:', error)
        }
    }

    async function getUser() {
        try {
            const response = await api.getUser()
            setUser(response)
        } catch (error) {
            console.error('Erro ao buscar informações do usuário:', error)
        }
    }

    async function getResume(project_id: string) {
        try {
            const response = await documentApi.getResume(project_id)
            return response
        } catch (error) {
            console.error('Erro ao buscar resumo do projeto:', error)
            return null
        }
    }

    useEffect(() => {
        const token = cookieService.getAuthData().access_token
        let socket: WebSocket | null = null
        let pingInterval: NodeJS.Timeout | null = null
        let pongReceived = true
        let isConnecting = false

        let shouldReconnect = true
        let jaAbriu = false // a 1ª abertura não é reconexão: a tela já leu o estado

        const connectWebSocket = () => {
            if (!shouldReconnect) {
                return
            }

            if (isConnecting) {
                console.log('Já tentando conectar, ignorando nova tentativa...')
                return
            }

            isConnecting = true
            console.log('Iniciando conexão WebSocket...')

            if (socket) {
                socket.close()
            }
            if (pingInterval) {
                clearInterval(pingInterval)
                pingInterval = null
            }

            let wasOpened = false

            socket = new WebSocket(
                `${import.meta.env.VITE_WS_CORE_URL}/ws?token=${token}`
            )

            socket.onopen = () => {
                console.log('Conexão WebSocket aberta!')
                wasOpened = true
                isConnecting = false
                pongReceived = true
                setWsAberto(true)
                if (jaAbriu) setWsReconexoes((n) => n + 1)
                jaAbriu = true

                pingInterval = setInterval(() => {
                    if (!pongReceived) {
                        console.log('Pong não recebido, reconectando...')
                        if (socket) {
                            socket.close()
                        }
                        return
                    }

                    if (socket && socket.readyState === WebSocket.OPEN) {
                        pongReceived = false
                        socket.send('ping')
                        // console.log('Ping enviado')
                    }
                }, 60000)
            }

            socket.onclose = (event) => {
                isConnecting = false
                setWsAberto(false)
                if (pingInterval) {
                    clearInterval(pingInterval)
                    pingInterval = null
                }

                if (!wasOpened) {
                    console.log(
                        'Conexão WebSocket recusada, parando tentativas de reconexão.',
                        event.code,
                        event.reason
                    )
                    shouldReconnect = false
                    return
                }

                console.log(
                    'Conexão WebSocket fechada, tentando reconectar...',
                    event.code,
                    event.reason
                )

                setTimeout(() => {
                    connectWebSocket()
                }, 3000)
            }

            socket.onerror = (error) => {
                console.log('Erro na conexão WebSocket:', error)
                isConnecting = false
            }

            socket.onmessage = (event) => {
                console.log('Mensagem recebida:', event.data)

                if (event.data === 'pong') {
                    pongReceived = true
                    // console.log('Pong recebido')
                    return
                }

                try {
                    const message = JSON.parse(event.data)

                    const notifyIfAway = (
                        moduleName: string,
                        navigationState?: Record<string, unknown>
                    ) => {
                        const config = MODULE_NOTIFICATION_CONFIG[moduleName]
                        if (!config) return
                        const currentPath = locationRef.current.pathname
                        const isOnPage = config.routes.some((r) =>
                            currentPath === r || currentPath.startsWith(r + '/')
                        )
                        if (isOnPage) return

                        const toastId = `${moduleName}-${message.project_id || 'default'}`
                        const description = message.description || 'Processamento atualizado'
                        const content = (
                            <div>
                                <div style={{fontWeight: 600, fontSize: '0.9rem'}}>
                                    {config.label}
                                </div>
                                <div style={{fontSize: '0.82rem', marginTop: 3, opacity: 0.85}}>
                                    {description}
                                </div>
                                <div style={{fontSize: '0.75rem', marginTop: 5, opacity: 0.6}}>
                                    Clique para abrir →
                                </div>
                            </div>
                        )

                        const handleToastClick = () => {
                            if (navigationState?.project_id) {
                                localStorage.setItem(
                                    'projectId',
                                    navigationState.project_id as string
                                )
                            }
                            navigateRef.current(config.primaryRoute, {
                                state: navigationState,
                            })
                        }

                        if (toast.isActive(toastId)) {
                            toast.update(toastId, {render: content})
                        } else {
                            toast.info(content, {
                                toastId,
                                onClick: handleToastClick,
                                closeOnClick: true,
                                autoClose: 10000,
                            })
                        }
                    }

                    if (message.module === 'document-analyzer') {
                        console.log(
                            'Mensagem de document analyzer recebida:',
                            message
                        )
                        setRedSocketMessage(message)
                        notifyIfAway('document-analyzer')
                    } else if (message.module === 'ata-inteligente') {
                        console.log(
                            'Mensagem do Ata Inteligente recebida:',
                            message
                        )
                        setCaronaSocketMessage(message)
                        notifyIfAway('ata-inteligente')
                    } else if (message.module === 'chat') {
                        console.log('Mensagem de chat recebida:', message)
                        if (
                            message.attached === true &&
                            message.status === 'Success'
                        ) {
                            // Upload concluido: o backend ja gravou o conteudo
                            // real + documentos; refaz o fetch no lugar de
                            // incluir (a notificacao nao tem author_id).
                            if (
                                message.thread_id &&
                                message.thread_id === selectedRef.current?.guid
                            ) {
                                getMessagesRef.current({keepOnError: true})
                            }
                        } else {
                            includeMessageAtArray(message)
                        }
                        notifyIfAway('chat')
                    } else if (message.module === 'document-generator') {
                        console.log('Mensagem de documento recebida:', message)
                        setWebSocketMessage(message)
                        notifyIfAway('document-generator', {
                            project_id: message.project_id,
                        })
                    } else if (message.module === 'price-research') {
                        console.log(
                            'Mensagem da Pesquisa de Preços recebida:',
                            message
                        )
                        setPriceResearchSocketMessage(message)
                        notifyIfAway('price-research')
                    } else if (message.module === 'radar-fornecedor') {
                        setRadarSocketMessage(message)
                        notifyIfAway('radar-fornecedor')
                    } else if (message.module === 'levantamento-mercado') {
                        setLevantamentoSocketMessage(message)
                        notifyIfAway('levantamento-mercado')
                    } else if (message.module === 'editor-template-flow') {
                        console.log(
                            'Mensagem do Editor de Fluxo recebida:',
                            message
                        )
                        notifyIfAway('editor-template-flow', {
                            document_flow_id: message.document_flow_id,
                            organization_uuid: message.organization_uuid,
                        })
                    } else if (message.module === 'chat-export') {
                        // Ignora ids nao pendentes nesta sessao (duplicados / outras abas).
                        if (!pendingExportsRef.current.has(message.export_id))
                            return
                        finishChatExport(message.export_id)
                        if (message.status === 'READY' && message.url) {
                            const link = document.createElement('a')
                            link.href = message.url
                            link.setAttribute('download', '')
                            link.target = '_blank'
                            link.rel = 'noopener'
                            link.style.display = 'none'
                            document.body.appendChild(link)
                            link.click()
                            document.body.removeChild(link)
                        } else {
                            toast.error(CHAT_EXPORT_ERROR)
                        }
                    } else if (message.module === 'assistente') {
                        setAssistenteSocketMessage(message)
                    } else if (message.module === 'document-upload') {
                        setDocumentUploadSocketMessage(message)
                    } else if (message.module === 'try-get-answers') {
                        setTryGetAnswersSocketMessage(message)
                    } else {
                        console.log('Tipo de mensagem desconhecido:', message)
                    }
                } catch (error) {
                    const errorMessage = (error as any).message
                    console.error('Erro ao parsear JSON:', errorMessage)
                    console.error('Dados recebidos:', event.data)
                }
            }
        }

        connectWebSocket()

        return () => {
            if (pingInterval) {
                clearInterval(pingInterval)
            }
            if (socket) {
                socket.close()
            }
        }
    }, [])

    const finishChatExport = (exportId: string) => {
        clearTimeout(pendingExportsRef.current.get(exportId))
        pendingExportsRef.current.delete(exportId)
        setPendingChatExports((ids) => ids.filter((id) => id !== exportId))
    }
    const registerChatExport = (exportId: string) => {
        pendingExportsRef.current.set(
            exportId,
            setTimeout(() => {
                finishChatExport(exportId)
                toast.error(CHAT_EXPORT_TIMEOUT_ERROR)
            }, CHAT_EXPORT_TIMEOUT_MS)
        )
        setPendingChatExports((ids) => [...ids, exportId])
    }

    // Refs para o closure do WebSocket (montado uma unica vez) enxergarem o
    // `selected` e o `getMessages` atuais.
    const selectedRef = useRef(selected)
    const getMessagesRequestIdRef = useRef(0)
    const getMessagesRef = useRef(getMessages)
    useLayoutEffect(() => {
        selectedRef.current = selected
        getMessagesRef.current = getMessages
    })

    useEffect(() => {
        getMessages()
    }, [selected])

    useEffect(() => {
        getThreads()
        getLimits()
        getAssistants()
        getThreadsArchived()
        getSubscription()
        getUser()
    }, [api])

    useEffect(() => {
        getArchivedProjects()
        getUserInfo()
        getArchivedPesquisas()
    }, [documentApi])

    useEffect(() => {
        if (
            webSocketMessage.status === 'FINISHED' ||
            webSocketMessage.description.includes('Error')
        ) {
            documentApi.getProjectById(webSocketMessage.project_id)
        }
    }, [webSocketMessage])

    return (
        <HistoryContext.Provider
            value={{
                pendingChatExports,
                registerChatExport,
                selected,
                setSelected,
                historyArray,
                createNewChat,
                messagesArray,
                includeMessageAtArray,
                tribunal,
                setTribunal,
                assistant,
                setAssistant,
                limits,
                assistants,
                getLimits,
                archiveThread,
                unarchiveThread,
                deleteThread,
                renameThread,
                updateCurrentThread,
                updateThreadUploadedDocuments,
                updateThreadPageCount,
                archivedThread,
                goToArchivedThreadsPage,
                archivedThreadPage,
                subscription,
                alertVisibility,
                unarchieveModal,
                setUnarchieveModal,
                isChat,
                area,
                setArea,
                projectTypes,
                projectTypeFilterId,
                setIsChat,
                projectArray,
                setMessagesArray,
                questionArray,
                setQuestionArray,
                getNextQuestion,
                getProjects,
                postProject: (projectData) => postProject(projectData),
                usersOrg,
                archiveProject,
                archivedProjects,
                goToArchivedProjectsPage,
                archivedProjectsPage,
                unarchiveProject,
                archivedPesquisas,
                setArchivedPesquisas,
                archivePesquisa,
                unarchivePesquisa,
                getArchivedPesquisas,
                goToArchivedPesquisasPage,
                archivedPesquisasPage,
                webSocketMessage,
                openModelAlertLimit,
                setOpenModelAlertLimit,
                orgUsersMetrics,
                getOrgUsersMetrics,
                loadingMessageBot,
                setLoadingMessageBot,
                userDocumentsInfo,
                uuid,
                setUuid,
                updateProject: (projectData) => updateProject(projectData),
                setOrganizationMaxUsages,
                projectCost,
                setProjectCost,
                setSelectedFiles,
                selectedFiles,
                user,
                viewResume,
                setViewResume,
                setRedSocketMessage,
                redSocketMessage,
                priceResearchSocketMessage,
                setPriceResearchSocketMessage,
                wsAberto,
                wsReconexoes,
                caronaSocketMessage,
                setCaronaSocketMessage,
                setRadarSocketMessage,
                radarSocketMessage,
                assistenteSocketMessage,
                setAssistenteSocketMessage,
                documentUploadSocketMessage,
                setDocumentUploadSocketMessage,
                tryGetAnswersSocketMessage,
                setTryGetAnswersSocketMessage,
                levantamentoSocketMessage,
                setLevantamentoSocketMessage,
                flowQuestions,
                setFlowData,
                flows,
                setFlows,
                updateFlow,
                getFlow,
            }}
        >
            {children}
        </HistoryContext.Provider>
    )
}

export default HistoryProvider
